/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { useState, useCallback, type ReactNode, useEffect, useRef, useMemo, Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type RecaptchaResult } from '@atlassian/google-recaptcha';
import OriginTracer from '@atlassiansox/origin-tracing';
import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalytics } from '../../analytics';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { hasGmailDomain } from '../../utils';
import { messages } from '../i18n';
import {
	Integration,
	type InvitePeopleFormCustomRef,
	type InvitePeopleComponentProps,
	type SubmitButtonRef,
} from '../../types';
import { InstrumentedCustomThemeButton as InstrumentedButton } from '../../analytics/InstrumentedButton';
import { useThirdPartyState, SlackWorkspacesDialog } from '../ThirdParty';
import { DropdownMenu } from '../DropdownMenu';
import FormTitle from './FormTitle';
import { inviteFormSubmitted } from '../../analytics/ufoExperiences';
import InvitePeopleForm from './InvitePeopleForm';
import { useInviteeListStateContext } from '../InviteeList';
import { useExperimentalFeatures } from '../../experiments/experimentalFeatures';
import { useInvitePeopleContext } from './InvitePeopleContext';
import { useCoreInvitesJiraProjectContext } from '../../experiments/coreInvitesInJiraProjects/CoreInvitesJiraProjectContext';
import {
	InvitePeoplePluginsAnalytics,
	PluginRenderer,
	pluginsOnValidate,
	useInvitePeopleData,
} from './plugins';
import { hasError, determineValidationState } from '../InviteeList/validate';
import { CloseButton } from '@atlaskit/modal-dialog';
import { CopyLinkButton } from '@atlaskit/share';
import LinkIcon from '@atlaskit/icon/core/link';

const styles = cssMap({
	formWrapper: {
		position: 'relative',
		maxWidth: '354px',
		marginInline: 'auto',
	},
	formHeader: {
		flexDirection: 'row-reverse',
	},
	formHeaderMenu: {
		alignSelf: 'flex-end',
		flexDirection: 'row-reverse',
	},
	formTitleBylineWrapper: {
		marginRight: token('space.400', '32px'),
		// @ts-ignore
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		h3: {
			fontWeight: token('font.weight.medium', '400'),
		},
	},
	formTitleByline: {
		fontWeight: token('font.weight.regular', '400'),
		marginTop: token('space.0', '0px'),
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	formDescriptionWrapper: {
		marginTop: token('space.100', '8px'),
		marginBottom: token('space.200', '16px'),
	},
	formFooter: {
		marginBottom: token('space.300', '24px'),
	},
	buttonWrapper: {
		marginBottom: token('space.200', '16px'),
	},
	rightAlignedButtonWrapper: {
		justifyContent: 'flex-end',
	},
	copyLinkWithRightAlignedButtons: {
		justifyContent: 'space-between',
		alignItems: 'center',
	},
});

interface ActionButtonsProps {
	hideCancelButton?: boolean;
	experimental_submitButtonRef?: SubmitButtonRef;
	isSendingInvitation: boolean;
	onCancelButtonClick?: () => void;
	source: string;
	productId: string;
	cancelButtonLabel?: ReactNode;
	getInviteButtonAnalyticsAttributes: () => any;
	recaptcha?: { isRecaptchaLoaded?: boolean; recaptchaLoadErrorReason?: string };
	handleSendButtonClick: () => void;
	getSubmitButtonText: () => ReactNode;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
	hideCancelButton,
	experimental_submitButtonRef,
	isSendingInvitation,
	onCancelButtonClick,
	source,
	productId,
	cancelButtonLabel,
	getInviteButtonAnalyticsAttributes,
	recaptcha,
	handleSendButtonClick,
	getSubmitButtonText,
}) => {
	return (
		<>
			{!(hideCancelButton || experimental_submitButtonRef) && (
				<InstrumentedButton
					testId="testId-invite-people-cancel-button"
					interactionName="invite-people-form.cancel"
					actionSubjectId={'cancelButton'}
					appearance="subtle"
					isDisabled={isSendingInvitation}
					onClick={onCancelButtonClick}
					analyticsAttributes={{
						integration: source,
						source,
						product: productId,
					}}
				>
					{cancelButtonLabel ? cancelButtonLabel : <FormattedMessage {...messages.cancelButton} />}
				</InstrumentedButton>
			)}
			<InstrumentedButton
				testId="testId-invite-people-submit-button"
				ref={experimental_submitButtonRef}
				actionSubjectId={'inviteButton'}
				analyticsAttributes={getInviteButtonAnalyticsAttributes}
				appearance="primary"
				form="invitePeopleForm"
				interactionName="invite-people-form.submit"
				isLoading={
					isSendingInvitation ||
					(!recaptcha?.isRecaptchaLoaded && !recaptcha?.recaptchaLoadErrorReason)
				}
				onClick={handleSendButtonClick}
				type="submit"
			>
				{getSubmitButtonText()}
			</InstrumentedButton>
		</>
	);
};

interface InviteModalWithCopyLinkBtnProps {
	actionButtonsProps: ActionButtonsProps;
	analytics: InvitePeopleAnalytics;
	experimental_copyLink?: string | undefined;
}

const InviteModalWithCopyLinkBtn: React.FC<InviteModalWithCopyLinkBtnProps> = ({
	actionButtonsProps,
	analytics,
	experimental_copyLink,
}) => {
	const intl = useIntl();
	const { productId } = useInvitePeopleContext();

	const { copyLink, copyLinkOrigin } = useMemo(() => {
		const rawCopyLink = experimental_copyLink || window.location.href;
		const origin = new OriginTracer({ product: productId });
		return {
			copyLink: origin.addToUrl(rawCopyLink),
			copyLinkOrigin: origin,
		};
	}, [productId, experimental_copyLink]);

	const handleCopyLink = useCallback(() => {
		analytics.copyLinkButtonClicked({
			originAttributes: copyLinkOrigin.toAnalyticsAttributes({ hasGeneratedId: true }),
		});
	}, [analytics, copyLinkOrigin]);

	return (
		<Inline
			space="space.100"
			alignBlock="center"
			xcss={cx(styles.buttonWrapper, styles.copyLinkWithRightAlignedButtons)}
		>
			<CopyLinkButton
				link={copyLink}
				onLinkCopy={handleCopyLink}
				isDisabled={false}
				copyLinkButtonText={intl.formatMessage(messages.copyLinkButtonText)}
				copiedToClipboardText={intl.formatMessage(messages.copiedToClipboardMessage)}
				iconBefore={<LinkIcon label="" />}
			/>
			<Inline space="space.100" xcss={styles.rightAlignedButtonWrapper}>
				<ActionButtons {...actionButtonsProps} />
			</Inline>
		</Inline>
	);
};

interface OwnProps extends InvitePeopleComponentProps {
	isSendingInvitation: boolean;
	maxNumberOfInputs: number;
	onCancelButtonClick?: () => void;
	onInviteButtonClick: (
		isSuccessModal: false,
		getRecaptchaToken?: RecaptchaResult['getRecaptchaToken'],
	) => Promise<void> | void;
	onInviteeListChange: (emails: string[]) => void;
	sendButtonLabel?: ReactNode;
	uniqueEmailDomains: string[];
	selectedProducts: string[];
	showSecondarySection: boolean;
}

const InvitePeopleComponent: React.FC<OwnProps> = ({
	alignButtons,
	cancelButtonLabel,
	experimental_formTitleByline,
	experimental_submitButtonRef,
	experimental_copyLink,
	formDescription,
	formTitle,
	hideFormHeader,
	hideCancelButton,
	isSendingInvitation,
	maxNumberOfInputs,
	onCancelButtonClick,
	onInviteButtonClick,
	onInviteeListChange,
	productOptions,
	showProductSelect,
	selectedProducts,
	sendButtonLabel: customSendButtonLabel,
	showSecondarySection,
	titleId,
	onAdminMenuItemClick,
}) => {
	const analytics = useAnalytics(InvitePeopleAnalytics);
	const pluginAnalytics = useAnalytics(InvitePeoplePluginsAnalytics);
	const invitePeopleData = useInvitePeopleData();
	const [showEmptyError, setShowEmptyError] = useState(false);
	const invitePeopleFormRef = useRef<InvitePeopleFormCustomRef>(null);
	const {
		invitePromptOnLinkV3Cohort,
		jiraSubProduct,
		loggedInEmailDomain,
		plugins,
		productId,
		source,
		userRole,
		userEmail,
	} = useInvitePeopleContext();
	const { isCoreInvitesInJiraProjectEnabled } = useCoreInvitesJiraProjectContext();

	const {
		inviteFromAssignModal: inviteFromAssignModalCohort,
		restoreInviteButton: restoreInviteButtonExperimentEnabled,
		showConfluenceCopyLink: showConfluenceCopyLinkEnabled,
	} = useExperimentalFeatures();
	const {
		isOk: enableThirdPartyInvites,
		integrations: thirdPartyIntegrations,
		orgSettings: thirdPartyOrgSettings,
	} = useThirdPartyState();
	const [recaptcha, setRecaptcha] = useState<RecaptchaResult | undefined>();

	const { emails, ids } = useInviteeListStateContext();
	useEffect(() => {
		analytics.inviteComponentViewed({
			hasGmailDomain: hasGmailDomain(loggedInEmailDomain),
			isConnectedToSlack: thirdPartyIntegrations.has(Integration.PLATFORM_SLACK_SERVICE),
			...thirdPartyOrgSettings,
		});
	}, [analytics, loggedInEmailDomain, thirdPartyIntegrations, thirdPartyOrgSettings]);

	const isBasicUser = userRole === 'basic';
	const shouldRenderProductSelect =
		showProductSelect &&
		// Display empty product selector when we are unable to determine
		// the defaultProducts that should be selected.
		(selectedProducts?.length === 0 ||
			(productOptions &&
				productOptions.length > 1 &&
				// show for trusted|admin users
				(!isBasicUser ||
					// OR if Product is Jira and there's no subproduct explicitly passed in
					(productId === 'jira' && !jiraSubProduct) ||
					// OR if Product is 'platform', which indicates that the caller doesn't
					// know what product subscriptions are available.
					productId === 'platform')));

	/**
	 * Checks the internal inputs from Invite People (inviteeList and validationState from the user picker), without checking plugins validation.
	 * Plugins validation is done through `pluginsOnValidate`.
	 */
	const isInternalFormDataValid = () => {
		const inputValues = [...emails, ...ids];
		const isInviteesEmpty = !inputValues.filter((item) => item.length).length;
		const isProductsEmpty = shouldRenderProductSelect && selectedProducts.length === 0;

		const validationList = determineValidationState(
			emails,
			ids,
			maxNumberOfInputs,
			userEmail,
			isCoreInvitesInJiraProjectEnabled,
		);

		if (isInviteesEmpty || isProductsEmpty) {
			if (isInviteesEmpty && invitePeopleFormRef.current) {
				invitePeopleFormRef.current.focusInviteeList();
			} else if (isProductsEmpty && invitePeopleFormRef.current) {
				invitePeopleFormRef.current.focusProductSelect();
			}
			setShowEmptyError(true);
			return false;
		}
		// We also want to cap the number of emails/ids allowed to maxNumberOfInputs
		if (inputValues.filter((item) => item.length).length > maxNumberOfInputs) {
			if (isInviteesEmpty && invitePeopleFormRef.current) {
				invitePeopleFormRef.current.focusInviteeList();
			}
			return false;
		}
		// When any other error occurs, redirect focus to the first input so the user can re-navigate through the form
		if (isInviteesEmpty && invitePeopleFormRef.current) {
			invitePeopleFormRef.current.focusInviteeList();
		}
		// If the there is an error state from the the user picker then the button is disabled
		return !hasError(validationList);
	};

	const getInviteButtonAnalyticsAttributes = () => {
		const inputValues = [...emails, ...ids];
		let formError;

		// TODO: remove `formError` from `inviteButton button clicked` event and create a new `validation` event with `formError` and the plugins validation data.
		//       https://atlassian.slack.com/archives/C0138SD4YTH/p1669849389985509?thread_ts=1669768434.466429&cid=C0138SD4YTH
		if (!isInternalFormDataValid()) {
			if (selectedProducts.length === 0) {
				formError = fg('product-terminology-refresh')
					? messages.noProductSelectedMessageAltAppify.defaultMessage
					: messages.noProductSelectedMessage.defaultMessage;
			} else {
				formError = inputValues.filter((val) => val.length).length
					? messages.invalidEmailMessage.defaultMessage
					: messages.emptyEmailMessage.defaultMessage;
			}
		} else {
			formError = undefined;
		}

		return {
			product: productId,
			numberOfEmails: emails.length,
			numberOfUniqueEmails: [...new Set(emails)].length,
			formError,
			invitedProducts: selectedProducts,
			integration: source,
			source,
			inviteFromAssignModalCohort,
		};
	};

	const handleSendButtonClick = async () => {
		if (!isInternalFormDataValid()) {
			return;
		}

		const { isValid } = await pluginsOnValidate({
			analytics: pluginAnalytics,
			invitePeopleData,
			plugins,
		});

		if (!isValid) {
			return;
		}

		inviteFormSubmitted.addMetadata({
			isThirdPartyIntegrated: Boolean(thirdPartyIntegrations.size > 0),
		});
		inviteFormSubmitted.start();
		await onInviteButtonClick(false, recaptcha?.getRecaptchaToken);
	};

	const getUniqueValidEmails = useCallback(() => {
		return [...new Set(emails)];
	}, [emails]);

	const handleProductSelectMenuOpen = () => {
		analytics.productSelectDropdownOpened({
			productOptions: productOptions?.map((product) => product.value),
			selectedProducts,
		});
	};

	const getSubmitButtonText = () => {
		if (customSendButtonLabel) {
			return customSendButtonLabel;
		}
		return (
			<FormattedMessage
				{...messages.sendInviteButton}
				values={{
					numberOfInputs: getUniqueValidEmails().length + ids.length,
				}}
			/>
		);
	};
	const actionButtonsProps: ActionButtonsProps = {
		hideCancelButton,
		experimental_submitButtonRef,
		isSendingInvitation,
		onCancelButtonClick,
		source,
		productId,
		cancelButtonLabel,
		getInviteButtonAnalyticsAttributes,
		recaptcha,
		handleSendButtonClick,
		getSubmitButtonText,
	};

	return (
		<Box testId="testId-invite-people-form">
			<Box xcss={styles.formWrapper}>
				{enableThirdPartyInvites && <SlackWorkspacesDialog />}
				{!hideFormHeader && (
					<Inline spread="space-between" xcss={styles.formHeader}>
						<Inline space="space.100" xcss={styles.formHeaderMenu}>
							{onCancelButtonClick && <CloseButton onClick={onCancelButtonClick} />}
							<DropdownMenu onAdminMenuItemClick={onAdminMenuItemClick} />
						</Inline>
						{formTitle ? (
							formTitle
						) : (
							<FormTitle
								productOptions={productOptions}
								selectedProducts={selectedProducts}
								titleId={titleId}
							/>
						)}
					</Inline>
				)}
				{!!invitePromptOnLinkV3Cohort && experimental_formTitleByline && (
					<Inline xcss={styles.formTitleBylineWrapper}>
						<Heading size="medium">{experimental_formTitleByline}</Heading>
					</Inline>
				)}
				<Box xcss={styles.formDescriptionWrapper} testId="invite-people-form-description-wrapper">
					<Text id="invite-people-form-description">{formDescription}</Text>
				</Box>
				<PluginRenderer plugins={plugins} location="header" />
				<InvitePeopleForm
					shouldRenderProductSelect={shouldRenderProductSelect}
					productOptions={productOptions}
					handleProductSelectMenuOpen={handleProductSelectMenuOpen}
					showEmptyError={showEmptyError}
					onCancelButtonClick={onCancelButtonClick}
					maxNumberOfInputs={maxNumberOfInputs}
					onInviteeListChange={onInviteeListChange}
					selectedProducts={selectedProducts}
					showSecondarySection={showSecondarySection}
					recaptcha={recaptcha}
					setRecaptcha={setRecaptcha}
					ref={invitePeopleFormRef}
				/>
				<Box xcss={styles.formFooter}>
					{restoreInviteButtonExperimentEnabled || showConfluenceCopyLinkEnabled ? (
						<InviteModalWithCopyLinkBtn
							actionButtonsProps={actionButtonsProps}
							analytics={analytics}
							experimental_copyLink={experimental_copyLink}
						/>
					) : (
						<Inline
							space="space.100"
							xcss={cx(
								styles.buttonWrapper,
								alignButtons === 'right' && styles.rightAlignedButtonWrapper,
							)}
						>
							<ActionButtons {...actionButtonsProps} />
						</Inline>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default InvitePeopleComponent;
